import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { api } from '../api/client';
import { Button, Card, Field, Form, Notice } from '../components';
import { useAction } from '../hooks';
import { useSession } from '../auth/session';

/**
 * Change your own password.
 *
 * Reached voluntarily, or forced when `mustChangePassword` is set — an account whose
 * password was issued by someone else is one that person can still sign into.
 *
 * The current password is required even though the user is already signed in. Without it,
 * a borrowed session is enough to lock the real owner out of their own account.
 */
export function ChangePassword({ forced = false }: { forced?: boolean }): JSX.Element {
  const { refresh } = useSession();
  const navigate = useNavigate();
  const { run, error, busy } = useAction();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);

  // Checked here as a courtesy so the user is not told by a round trip; the server
  // enforces length itself, because this check is only advice.
  const mismatch = confirm !== '' && next !== confirm;
  const tooShort = next !== '' && next.length < 12;

  const submit = async (): Promise<void> => {
    const ok = await run(async () => {
      await api.post('/auth/change-password', { currentPassword: current, newPassword: next });
    });

    if (!ok) return;
    setDone(true);
    // The session survives the change — every *other* session is revoked, but the one the
    // user is standing in is kept, so they are not signed out of the screen they are on.
    await refresh();
    if (forced) navigate('/');
  };

  return (
    <div className={forced ? 'signin' : ''}>
      <div className={forced ? 'signin-card' : ''}>
        <Card title="Change password">
          {forced && (
            <Notice kind="info">
              Your password was issued by an administrator. Choose your own before continuing.
            </Notice>
          )}
          {error !== null && <Notice kind="error">{error}</Notice>}
          {done && <Notice kind="success">Password changed. Other sessions have been signed out.</Notice>}

          <Form onSubmit={() => void submit()}>
            <Field label="Current password" value={current} onChange={setCurrent} type="password" required />
            <Field
              label="New password"
              value={next}
              onChange={setNext}
              type="password"
              required
              hint="At least 12 characters. Length matters more than symbols."
            />
            <Field label="Confirm new password" value={confirm} onChange={setConfirm} type="password" required />

            {mismatch && <Notice kind="error">The two new passwords do not match.</Notice>}
            {tooShort && <Notice kind="error">A password must be at least 12 characters long.</Notice>}

            <div className="form-actions">
              <Button
                type="submit"
                disabled={busy || current === '' || next === '' || mismatch || tooShort}
              >
                {busy ? 'Changing…' : 'Change password'}
              </Button>
            </div>
          </Form>
        </Card>
      </div>
    </div>
  );
}
